import type { ReactNode } from "react";

import { ApiError } from "../api/errors";
import type { ProductStatus } from "../api/types";

/** Lifecycle status, in the backend's own vocabulary. Never re-worded: an
 *  administrator who reads "Live" here and "published" in a support thread has
 *  to translate between them. */
export function StatusBadge({ status }: { status: ProductStatus | string }) {
  return <span className={`badge badge--${status}`}>{status.replace("_", " ")}</span>;
}

export function Panel({
  title,
  actions,
  children,
  bodyClass,
}: {
  title: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  bodyClass?: string;
}) {
  return (
    <section className="panel">
      <header className="panel__head">
        <h2 className="panel__title">{title}</h2>
        {actions ? <div className="row">{actions}</div> : null}
      </header>
      <div className={bodyClass ?? "panel__body"}>{children}</div>
    </section>
  );
}

export function ErrorNotice({ error }: { error: unknown }) {
  const message =
    error instanceof ApiError
      ? error.userMessage
      : "Something went wrong. Try again.";
  return (
    <div className="notice notice--error" role="alert">
      {message}
    </div>
  );
}

/** Money as the backend stores it: an integer count of minor units.
 *
 * Formatting divides by 100 for display only. Nothing converts back through a
 * float — the editor keeps and submits the integer. */
export function formatMoney(minorUnits: number, currency: string): string {
  const major = Math.trunc(minorUnits / 100);
  const minor = Math.abs(minorUnits % 100);
  return `${major}.${String(minor).padStart(2, "0")} ${currency}`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}
